import React from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useFrame} from 'context/frame';
import {VideoConstraints, WebcamProps, WebcamRefTypes} from '../Webcam';
import cameraIcon from 'assets/icons/camera-icon.svg';
import {CustomIcon} from '@guestapp/ui';
import {DeviceCamera} from './DeviceCamera/DeviceCamera';
import {
  BackIconButton,
  HelperText,
  StyledWebcam,
  Subtitle,
  TakePhotoButton,
  Title,
  Wrapper,
  ButtonsSection,
} from './styled';

const getAndMapFacingMode = (constraints: VideoConstraints) => {
  if (typeof constraints === 'boolean' || !constraints) return;

  if (constraints.facingMode === 'user') {
    return 'user';
  } else if (constraints.facingMode === 'environment') {
    return 'environment';
  }
};

export type MobileScreenshotPayLoad =
  | {
      isNative: true;
      photo?: Promise<string>;
    }
  | {isNative: false};

export type MobileCameraProps = WebcamProps & {
  onScreenshot: (payload: MobileScreenshotPayLoad) => void;
  disabled: boolean;
  text?: React.ReactNode | string;
  title?: React.ReactNode | string;
};
const MobileWebcam = React.forwardRef<WebcamRefTypes, MobileCameraProps>(
  ({onScreenshot, title, text, disabled, ...props}, ref) => {
    const navigate = useNavigate();
    const {document: frameDocument} = useFrame();
    const rootElement = frameDocument?.body || document.body;
    const {t} = useTranslation();
    const onBack = () => navigate(-1);

    const handleNativeScreenshot = React.useCallback(
      (photo?: Promise<string>) => onScreenshot({isNative: true, photo: photo}),
      [onScreenshot],
    );

    return createPortal(
      <Wrapper className="mobile-camera">
        <BackIconButton onClick={onBack} className="mobile-camera__back-btn">
          <CustomIcon size={20} name="backBlueArrow" />
        </BackIconButton>
        {title && <Title className="mobile-camera__title">{title}</Title>}
        {text ? (
          <Subtitle className="mobile-camera__subtitle">{text}</Subtitle>
        ) : (
          <HelperText className="mobile-camera__helper">
            {t('place_document_inside_frame')}
          </HelperText>
        )}
        <StyledWebcam ref={ref} {...props} />
        <ButtonsSection>
          <TakePhotoButton
            disabled={disabled}
            onClick={() => onScreenshot({isNative: false})}
            iconSrc={cameraIcon}
            iconAlt={t('take_photo')}
            className="mobile-camera__take-btn"
          />
          <DeviceCamera
            onChange={handleNativeScreenshot}
            facingMode={getAndMapFacingMode(props.additionalConstraints)}
          />
        </ButtonsSection>
      </Wrapper>,
      rootElement,
    );
  },
);

export {MobileWebcam};
